import { FormEvent, useState } from 'react';
import type { User, FamilyRole, RecurrenceRule } from '@kinapp/shared';
import { api } from '../lib/api';
import { FormField } from './FormField';
import { DependentMultiSelect } from './DependentMultiSelect';
import { RecurrenceSelect } from './RecurrenceSelect';

interface CreateTaskFormProps {
  familyId: string;
  dependents: Array<{ id: string; name: string }>;
  familyMembers: Array<{ user: User; role: FamilyRole }>;
  onSuccess: () => void;
  onCancel: () => void;
  initialDueAt?: string;
}

export function CreateTaskForm({
  familyId,
  dependents,
  familyMembers,
  onSuccess,
  onCancel,
  initialDueAt,
}: CreateTaskFormProps) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [assignedToId, setAssignedToId] = useState('');
  const [dueAt, setDueAt] = useState(initialDueAt ?? '');
  const [recurrenceRule, setRecurrenceRule] = useState<RecurrenceRule | null>(null);
  const [selectedDepIds, setSelectedDepIds] = useState<string[]>(dependents.map((d) => d.id));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Extract date for smart recurrence labels
  const refDate = dueAt ? dueAt.slice(0, 10) : undefined;

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');

    if (!title.trim()) {
      setError('Title is required');
      return;
    }

    setLoading(true);

    try {
      await api.createTask({
        familyId,
        dependentId: selectedDepIds[0],
        dependentIds: selectedDepIds,
        title: title.trim(),
        description: description.trim() || undefined,
        assignedToId: assignedToId || undefined,
        dueAt: dueAt || undefined,
        recurrenceRule: recurrenceRule ?? undefined,
      });

      onSuccess();
    } catch (err: any) {
      setError(err.message || 'Failed to create task');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      {error && (
        <div className="rounded-md bg-red-50 p-4 mb-4">
          <p className="text-sm text-red-800">{error}</p>
        </div>
      )}

      <DependentMultiSelect dependents={dependents} selectedIds={selectedDepIds} onChange={setSelectedDepIds} />

      <FormField label="Title" id="task-title" required>
        <input
          type="text"
          id="task-title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          className="input"
          placeholder="e.g., Pick up medication"
          required
        />
      </FormField>

      <FormField label="Description" id="task-description">
        <textarea
          id="task-description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          className="input"
          rows={3}
          placeholder="Optional details..."
        />
      </FormField>

      <FormField label="Assigned to" id="task-assigned">
        <select
          id="task-assigned"
          value={assignedToId}
          onChange={(e) => setAssignedToId(e.target.value)}
          className="input"
        >
          <option value="">Unassigned</option>
          {familyMembers.map(({ user }) => (
            <option key={user.id} value={user.id}>
              {user.name}
            </option>
          ))}
        </select>
      </FormField>

      <FormField label="Due date" id="task-due">
        <input
          type="datetime-local"
          id="task-due"
          value={dueAt}
          onChange={(e) => setDueAt(e.target.value)}
          min={new Date().toISOString().slice(0, 16)}
          className="input"
        />
      </FormField>

      <FormField label="Recurrence" id="task-recurrence">
        <RecurrenceSelect value={recurrenceRule} onChange={setRecurrenceRule} referenceDate={refDate} />
      </FormField>

      <div className="flex gap-3 mt-8">
        <button
          type="button"
          onClick={onCancel}
          className="flex-1 btn btn-secondary"
          disabled={loading}
        >
          Cancel
        </button>
        <button
          type="submit"
          className="flex-1 btn btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
          disabled={loading}
        >
          {loading ? 'Creating...' : 'Create task'}
        </button>
      </div>
    </form>
  );
}
